import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export interface OperationsUser {
  id: string;
  subject: string;
  name: string;
  email: string;
  role: string;
}

interface AuthContextValue {
  user: OperationsUser | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  mode: 'portal' | 'standalone';
  signup: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function consumeLaunchToken(token: string) {
  const response = await fetch('/api/auth/launch', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || body.message || 'Portal launch could not be verified');
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<OperationsUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'portal' | 'standalone'>('portal');
  const [signup, setSignup] = useState(false);

  const refresh = useCallback(async () => {
    const response = await fetch('/api/auth/me', { credentials: 'same-origin' });
    if (!response.ok) {
      setUser(null);
      return;
    }
    const payload = await response.json();
    setUser(payload.user || null);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const configurationResponse = await fetch('/api/auth/configuration', { credentials: 'same-origin' });
        if (!configurationResponse.ok) throw new Error('Authentication configuration is unavailable');
        const configuration = await configurationResponse.json();
        if (!['portal','standalone'].includes(configuration.mode)) throw new Error('Invalid authentication configuration');
        if (active) { setMode(configuration.mode); setSignup(configuration.signup === true); }
        const params = new URLSearchParams(window.location.search);
        const launchToken = params.get('launch_token');
        if (launchToken && configuration.mode === 'portal') {
          await consumeLaunchToken(launchToken);
          params.delete('launch_token');
          const query = params.toString();
          window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
        }
        await refresh();
        if (active) setError(null);
      } catch (requestError) {
        if (active) {
          setUser(null);
          setError(requestError instanceof Error ? requestError.message : 'Unable to establish an Operations session');
        }
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [refresh]);

  const logout = useCallback(async () => {
    const response = await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    if (!response.ok && response.status !== 401) throw new Error('Sign out failed. Please try again.');
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, isLoading, error, refresh, logout, mode, signup }), [user, isLoading, error, refresh, logout, mode, signup]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
